/**
 * RedlineStep — per-step before/after diff card (Phase 23.2.2 — Living SOPs)
 *
 * Renders a single step's change in the redline view. Covers all five change
 * types surfaced by the detail endpoint:
 *  - inserted: green tint on the AFTER panel, `+` icon, no BEFORE
 *  - deleted:  red strikethrough on the BEFORE panel, gray ghost AFTER
 *  - updated:  amber tint on AFTER, text diff via `DiffText`
 *  - reordered: blue arrow indicator in the step-number column
 *  - metadata-only: banner "No textual change — only metadata delta"
 *
 * Per-step checkboxes ("viewed" / "approve this step") are rendered as lifted
 * props so the parent page can drive the master "I have reviewed all modified
 * steps" gate. State is held in ProposalDetailPage, NOT here.
 *
 * Phase 23.4.2 — Evidence wire-up. Frame evidence now renders inline
 * (click-to-expand lightbox), clip evidence uses a native `<video controls>`
 * player, and frames paired with a source assessment surface a "Request clip"
 * button that enqueues a YOLO-blurred ~23s clip via
 * POST /api/sops/proposals/:id/request-clip. A companion "View source
 * assessment" link lets the trainer drill into the full context.
 *
 * Phase 23.4.3 — Evidence linkage. Frame and clip evidence rows now render a
 * citation strip beneath the media: `[03:42][45% compliance][Op. Lin][Trigger
 * A1B2 ↗]` — closes the existing TODO at `RedlineStep.tsx` (was the
 * 23.4.2-polish marker). When `scoreContext` is absent (V1 legacy proposals
 * or struggle-signal items), the strip falls back to today's "View source
 * assessment" link with a small `(legacy)` modifier.
 */

import { useState, useId } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Minus,
  Pencil,
  ArrowUpDown,
  ShieldAlert,
  AlertTriangle,
  ImageIcon,
  Video,
  Film,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { DiffText } from './DiffText';
import {
  useRequestEvidenceClip,
  type CalloutBox,
  type SnapshotStep,
  type SafetyFlag,
  type SignedEvidenceItem,
  type SopStepMediaAnnotations,
} from '../../../hooks/useSopProposals';
import { getScoreColor } from '../../../lib/score-colors';

export type RedlineChangeType =
  | 'inserted'
  | 'deleted'
  | 'updated'
  | 'reordered'
  | 'metadata-only';

export interface RedlineStepProps {
  /** Proposal id — needed by the Phase 23.4.2 Request Clip mutation. */
  proposalId: string;
  originalStep: SnapshotStep | null;
  proposedStep: SnapshotStep | null;
  changeType: RedlineChangeType;
  sectionTitle: string;
  taskTitle: string;
  evidence: SignedEvidenceItem[];
  safetyFlag?: SafetyFlag;
  viewed: boolean;
  approved: boolean;
  onViewedChange: (viewed: boolean) => void;
  onApprovedChange: (approved: boolean) => void;
  // Whether the page is in read-only mode (state != pending_review).
  readOnly?: boolean;
  /**
   * Whether the caller is allowed to trigger Phase 23.4.2 actions
   * (Request Clip). Mirrors the action-stack gate on the sidebar
   * (trainer+ and not read-only). Defaults to `!readOnly` for callers
   * that don't explicitly thread the role claim through.
   */
  canRequestClip?: boolean;
}

// Small utility — formats duration seconds to human-readable shorthand.
function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const remSecs = seconds % 60;
  return remSecs === 0 ? `${mins}m` : `${mins}m ${remSecs}s`;
}

function ChangeIcon({ changeType }: { changeType: RedlineChangeType }) {
  if (changeType === 'inserted')
    return <Plus size={16} className="text-green-600" strokeWidth={2.5} />;
  if (changeType === 'deleted')
    return <Minus size={16} className="text-red-600" strokeWidth={2.5} />;
  if (changeType === 'reordered')
    return <ArrowUpDown size={16} className="text-blue-600" strokeWidth={2.5} />;
  if (changeType === 'updated' || changeType === 'metadata-only')
    return <Pencil size={16} className="text-amber-600" strokeWidth={2.5} />;
  return null;
}

const CHANGE_LABEL: Record<RedlineChangeType, string> = {
  inserted: 'New step',
  deleted: 'Deleted step',
  updated: 'Edited step',
  reordered: 'Reordered step',
  'metadata-only': 'Metadata-only change',
};

/**
 * Build the URL used by the "View source assessment" link.
 *
 * We route to the v1 assessment detail page (`/assessments/:id`) and include
 * the harvested timestamp as `?t=<seconds>` so a follow-up chunk can
 * auto-seek the embedded video player. Today the query param is ignored by
 * the page but is reserved for that purpose.
 */
function buildSourceAssessmentHref(
  assessmentId: string,
  timestamp: number | undefined
): string {
  const base = `/assessments/${assessmentId}`;
  if (timestamp === undefined || timestamp === null) return base;
  return `${base}?t=${Math.max(0, Math.floor(timestamp))}`;
}

/**
 * Phase 23.6.1 — Gemini 2.5 Pro bounding-box overlay.
 *
 * Coordinate discipline (pinned per Vertex AI docs + SimEdw Jul 2025):
 *   box_2d[0] = y0  (top)
 *   box_2d[1] = x0  (left)
 *   box_2d[2] = y1  (bottom)
 *   box_2d[3] = x1  (right)
 *   Units:       0-1000 normalized.
 *
 * The SVG `viewBox="0 0 1000 1000"` + `preserveAspectRatio="none"` pair maps
 * Gemini's raw 0-1000 coordinates directly to the `<img>`'s pixel-space rect
 * via CSS `position:absolute; inset:0` — no coordinate transformation needed.
 * DO NOT convert to pixels, percentages, or 0-1 at render time.
 */
function CalloutOverlay({ callouts }: { callouts: CalloutBox[] }) {
  if (callouts.length === 0) return null;
  return (
    <svg
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      {callouts.map((c, i) => {
        const [y0, x0, y1, x1] = c.box_2d;
        const width = Math.max(0, x1 - x0);
        const height = Math.max(0, y1 - y0);
        return (
          <g key={`${c.number}-${i}`}>
            <rect
              x={x0}
              y={y0}
              width={width}
              height={height}
              stroke="rgb(234, 88, 12)"
              strokeWidth={4}
              fill="rgba(234, 88, 12, 0.15)"
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={x0 + 10}
              y={y0 + 36}
              fill="rgb(234, 88, 12)"
              fontSize={28}
              fontWeight="bold"
              style={{ paintOrder: 'stroke fill' }}
              stroke="white"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            >
              {c.number}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Horizontal legend beneath a frame — "1 — valve V3 · 2 — pressure gauge".
 * Emits nothing when no callouts were generated.
 */
function CalloutLegend({ annotations }: { annotations: SopStepMediaAnnotations }) {
  if (annotations.callouts.length === 0) return null;
  return (
    <p className="max-w-[16rem] text-[11px] leading-snug text-gray-600">
      {annotations.callouts.map((c, i) => (
        <span key={`${c.number}-${i}`}>
          <span className="font-semibold text-orange-700">{c.number}</span>
          <span className="mx-1 text-gray-400">—</span>
          <span>{c.label}</span>
          {i < annotations.callouts.length - 1 && (
            <span className="mx-1.5 text-gray-300">·</span>
          )}
        </span>
      ))}
    </p>
  );
}

/**
 * Format a `sourceTimestamp` (seconds offset within source video) as `mm:ss`.
 * Already used elsewhere in the codebase via `?t=<seconds>` query params, but
 * the redline citation strip wants a human-readable mm:ss display.
 */
function formatTimestampMmSs(seconds: number | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Phase 23.4.3 — Citation strip for evidence items. Renders a row of pills:
 *   `[03:42] [45% compliance] [Op. Lin Wei] [Trigger A1B2 ↗]`
 *
 * Falls back to a `(legacy)` modifier on the existing "View source
 * assessment" link when `scoreContext` is absent (V1 evidence rows or
 * struggle-signal items that intentionally have no per-item citation).
 *
 * The trigger pill links to `/assessments/triggers/:sourceTriggerId` (existing
 * route at `apps/web/src/pages/assessments/TriggerDetailPage.tsx`), with a
 * fallback to `/assessments/:sourceAssessmentId` when only the assessment is
 * known. Multi-tenant safety: the destination route enforces `orgId` filtering
 * server-side (existing protection from V1 trigger detail handlers).
 */
function CitationStrip({ item }: { item: SignedEvidenceItem }) {
  // uploaded_image rows never carry a scoreContext (no source assessment by
  // construction). Skip the strip entirely.
  if (item.kind === 'uploaded_image') {
    return null;
  }

  const ctx = item.scoreContext;
  const ts = formatTimestampMmSs(item.sourceTimestamp);
  const truncatedTriggerId = item.sourceTriggerId
    ? item.sourceTriggerId.slice(0, 8).toUpperCase()
    : null;

  // Legacy proposals and struggle_signal items: fall back to today's
  // "View source assessment" link. The hasSource check upstream still
  // handles the broader presence guard — here we just provide the modifier
  // when the citation strip's enriched fields are unavailable.
  if (!ctx) {
    if (!item.sourceAssessmentId) return null;
    return (
      <Link
        to={buildSourceAssessmentHref(
          item.sourceAssessmentId,
          item.sourceTimestamp
        )}
        className="flex items-center gap-1 text-[11px] text-guild-green hover:underline"
      >
        <ExternalLink size={10} />
        View source assessment
        <span className="ml-1 italic text-gray-400">(legacy)</span>
      </Link>
    );
  }

  const operatorLabel = item.operatorName
    ? `Op. ${item.operatorName}`
    : 'Unknown operator';

  // Trigger pill destination — prefer trigger detail when available, fall
  // back to assessment detail when the proposal carries assessmentId only
  // (rare — perf-delta detector should always resolve a trigger row).
  const triggerHref = item.sourceTriggerId
    ? `/assessments/triggers/${item.sourceTriggerId}`
    : item.sourceAssessmentId
      ? buildSourceAssessmentHref(
          item.sourceAssessmentId,
          item.sourceTimestamp
        )
      : null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      {ts && (
        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium tabular-nums text-gray-700">
          {ts}
        </span>
      )}
      <span
        className={`rounded-full px-2 py-0.5 font-medium tabular-nums ${getScoreColor(
          ctx.complianceScore,
          'badge'
        )}`}
        title={`Compliance ${Math.round(ctx.complianceScore)}% · Safety ${Math.round(
          ctx.safetyScore
        )}% · Efficiency ${Math.round(ctx.efficiencyScore)}% · Overall ${Math.round(
          ctx.overallScore
        )}%`}
      >
        {Math.round(ctx.complianceScore)}% compliance
      </span>
      <span
        className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700"
        title={item.operatorName ?? 'Operator unresolved'}
      >
        {operatorLabel}
      </span>
      {truncatedTriggerId && triggerHref ? (
        <Link
          to={triggerHref}
          className="inline-flex items-center gap-1 rounded-full bg-guild-mint/40 px-2 py-0.5 font-medium text-guild-green hover:underline"
          title={
            item.sourceTriggerId
              ? `View trigger ${item.sourceTriggerId}`
              : 'View source assessment'
          }
        >
          Trigger {truncatedTriggerId}
          <ExternalLink size={10} />
        </Link>
      ) : null}
    </div>
  );
}

interface EvidenceAssetProps {
  item: SignedEvidenceItem;
  proposalId: string;
  /** True when a clip asset already exists for the same step, so "Request clip" is hidden. */
  clipAlreadyExistsForStep: boolean;
  /** Whether action buttons should be disabled (read-only / non-trainer view). */
  canRequestClip: boolean;
}

function EvidenceAsset({
  item,
  proposalId,
  clipAlreadyExistsForStep,
  canRequestClip,
}: EvidenceAssetProps) {
  const [expanded, setExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestClip = useRequestEvidenceClip();

  // Phase 23.4.3 closes the previous `TODO (23.4.2-polish)`: the citation
  // strip below renders timestamp / score / operator / trigger pills when
  // `scoreContext` is present, and falls back to a `(legacy)` modifier on
  // the existing "View source assessment" link when it is not (V1 evidence
  // or struggle-signal items).
  const hasSource = !!item.sourceAssessmentId;

  // Signed URL absent -> the media row exists but S3 retention expired (or
  // the signer failed). Render a placeholder + source link so the trainer
  // can still drill into the source assessment.
  if (!item.signedUrl) {
    return (
      <div className="flex flex-shrink-0 flex-col gap-1">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-md bg-gray-100"
          title="Media unavailable"
        >
          <ImageIcon size={18} className="text-gray-400" />
        </div>
        {hasSource && item.sourceAssessmentId && (
          <Link
            to={buildSourceAssessmentHref(
              item.sourceAssessmentId,
              item.sourceTimestamp
            )}
            className="flex items-center gap-1 text-[11px] text-guild-green hover:underline"
          >
            <ExternalLink size={10} />
            View source assessment
          </Link>
        )}
      </div>
    );
  }

  // ── Video clip ────────────────────────────────────────────────────────
  if (item.kind === 'video_clip') {
    return (
      <div className="flex flex-shrink-0 flex-col gap-1">
        <div className="relative">
          <video
            src={item.signedUrl}
            controls
            playsInline
            preload="metadata"
            className="h-36 w-64 rounded-md bg-black object-contain"
            aria-label="Evidence video clip"
          >
            Your browser does not support video playback.
          </video>
          {item.durationSeconds !== undefined &&
            item.durationSeconds !== null && (
              <span className="absolute right-1 top-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                <Film
                  size={10}
                  className="mr-0.5 inline-block"
                  strokeWidth={2.5}
                />
                {Math.round(item.durationSeconds)}s clip
              </span>
            )}
        </div>
        {item.caption && (
          <p className="max-w-[16rem] text-[11px] text-gray-500">
            {item.caption}
          </p>
        )}
        {hasSource && (
          <CitationStrip item={item} />
        )}
      </div>
    );
  }

  // ── Frame + uploaded_image ────────────────────────────────────────────
  const isFrame = item.kind === 'frame';
  const isUploaded = item.kind === 'uploaded_image';

  // Request-clip eligibility: must be a frame AND have a source assessment
  // (uploaded_image has no source video to clip from). The backend also
  // gates on cap_exceeded / not_a_frame / source_expired; we keep the
  // client-side gate lean to avoid duplicating those checks. Hide the
  // button entirely if a clip already exists for this step (prevents
  // "Request clip" from appearing next to a frame when its upgraded clip
  // is already in the strip).
  const canOfferClipUpgrade =
    isFrame &&
    hasSource &&
    !clipAlreadyExistsForStep &&
    canRequestClip;

  const handleRequestClip = () => {
    if (!item.id) {
      setErrorMessage(
        'Cannot request a clip for this frame (missing media id).'
      );
      return;
    }
    setErrorMessage(null);
    requestClip.mutate(
      { proposalId, mediaId: item.id },
      {
        onError: (err) => {
          const msg =
            err instanceof Error
              ? err.message
              : 'Could not request clip. Please try again.';
          setErrorMessage(msg);
        },
      }
    );
  };

  // Phase 23.6.1 — Gemini 2.5 Pro callouts. Frames only; uploaded_image rows
  // never carry annotations (different code path). Fall back to no overlay if
  // the backend hasn't populated annotations yet (callout fire-and-forget is
  // still in flight, or failed silently).
  const annotations: SopStepMediaAnnotations | null =
    isFrame && item.annotations ? item.annotations : null;

  return (
    <div className="flex flex-shrink-0 flex-col gap-1.5">
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="group relative rounded-md focus:outline-none focus:ring-2 focus:ring-guild-green/40"
        aria-label={item.caption ?? 'Open evidence image'}
      >
        <img
          src={item.signedUrl}
          alt={item.caption ?? 'Evidence image'}
          className="h-24 w-24 rounded-md object-cover"
          loading="lazy"
        />
        {annotations && <CalloutOverlay callouts={annotations.callouts} />}
      </button>
      {annotations && <CalloutLegend annotations={annotations} />}
      {item.caption && (
        <p className="max-w-[10rem] truncate text-[11px] text-gray-500">
          {item.caption}
        </p>
      )}
      {isUploaded && (
        <p className="text-[10px] italic text-gray-500">
          Uploaded by trainer
        </p>
      )}
      {hasSource && (
        <CitationStrip item={item} />
      )}
      {canOfferClipUpgrade && (
        <button
          type="button"
          onClick={handleRequestClip}
          disabled={requestClip.isPending}
          aria-label="Request a video clip for this step"
          title="Generate a short video clip centered on this frame."
          className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:border-guild-green/30 hover:bg-guild-mint/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {requestClip.isPending ? (
            <>
              <Loader2
                size={10}
                className="animate-spin"
                strokeWidth={2.5}
              />
              Generating clip (30-60s)...
            </>
          ) : (
            <>
              <Video size={10} strokeWidth={2.5} />
              Request clip
            </>
          )}
        </button>
      )}
      {errorMessage && (
        <p
          role="alert"
          className="max-w-[10rem] text-[11px] text-rose-700"
        >
          {errorMessage}
        </p>
      )}
      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setExpanded(false)}
        >
          <div
            className="relative inline-block"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={item.signedUrl}
              alt={item.caption ?? 'Evidence image'}
              className="block max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            />
            {annotations && <CalloutOverlay callouts={annotations.callouts} />}
          </div>
        </div>
      )}
    </div>
  );
}

export function RedlineStep({
  proposalId,
  originalStep,
  proposedStep,
  changeType,
  sectionTitle,
  taskTitle,
  evidence,
  safetyFlag,
  viewed,
  approved,
  onViewedChange,
  onApprovedChange,
  readOnly = false,
  canRequestClip,
}: RedlineStepProps) {
  const viewedId = useId();
  const approvedId = useId();

  // Default the Request Clip gate to "not read-only" when the caller does
  // not explicitly set it. ProposalDetailPage threads the role-based value
  // through; other callers (rare) get a sensible fallback.
  const clipActionAllowed = canRequestClip ?? !readOnly;

  // Determine whether ANY asset in this row's evidence strip is already a
  // video_clip. Used to hide the Request Clip button on sibling frames —
  // once an upgrade exists, the strip already shows the clip so offering
  // another upgrade is redundant (and the backend would 409 cap_exceeded
  // anyway).
  const hasClipForThisRow = evidence.some((e) => e.kind === 'video_clip');

  // Position label for the step-number column.
  const beforeNumber = originalStep?.stepNumber ?? null;
  const afterNumber = proposedStep?.stepNumber ?? null;

  const beforeText = originalStep?.description ?? '';
  const afterText = proposedStep?.description ?? '';

  const [showWordDiff, setShowWordDiff] = useState(false);

  // Classify background/border per change type. We cannot use dynamic Tailwind
  // classes, so we explicitly enumerate.
  const cardBorderColor =
    changeType === 'inserted'
      ? 'border-green-200'
      : changeType === 'deleted'
        ? 'border-red-200'
        : changeType === 'reordered'
          ? 'border-blue-200'
          : 'border-amber-200';

  const afterPanelBg =
    changeType === 'inserted'
      ? 'bg-green-50'
      : changeType === 'deleted'
        ? 'bg-gray-100'
        : changeType === 'reordered'
          ? 'bg-blue-50'
          : 'bg-amber-50';

  const beforePanelBg = changeType === 'deleted' ? 'bg-red-50' : 'bg-gray-50';

  return (
    <div
      className={`rounded-xl border-2 bg-white shadow-sm ${cardBorderColor}`}
      data-testid="redline-step"
    >
      {/* Header — breadcrumb + change-type label + step-number indicator */}
      <div className="flex items-start gap-3 border-b border-gray-100 p-4">
        <div className="flex flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
          <ChangeIcon changeType={changeType} />
          {changeType === 'reordered' && beforeNumber !== null && afterNumber !== null ? (
            <span className="ml-1 font-semibold tabular-nums text-blue-700">
              #{beforeNumber} → #{afterNumber}
            </span>
          ) : changeType === 'inserted' && afterNumber !== null ? (
            <span className="ml-1 font-semibold tabular-nums text-green-700">
              #{afterNumber}
            </span>
          ) : changeType === 'deleted' && beforeNumber !== null ? (
            <span className="ml-1 font-semibold tabular-nums text-red-700 line-through">
              #{beforeNumber}
            </span>
          ) : beforeNumber !== null ? (
            <span className="ml-1 font-semibold tabular-nums text-amber-700">
              #{beforeNumber}
            </span>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-gray-500">
            <span className="font-medium text-gray-700">{sectionTitle}</span>
            <span className="mx-1 text-gray-400">/</span>
            <span>{taskTitle}</span>
          </p>
          <p className="mt-0.5 text-sm font-semibold text-gray-900">
            {CHANGE_LABEL[changeType]}
          </p>
        </div>
        {changeType === 'updated' && (
          <button
            type="button"
            onClick={() => setShowWordDiff((v) => !v)}
            className="flex-shrink-0 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:border-guild-green/30 hover:bg-gray-50"
            aria-pressed={showWordDiff}
          >
            {showWordDiff ? 'Paragraph diff' : 'Word diff'}
          </button>
        )}
      </div>

      {/* Safety badge */}
      {safetyFlag && (
        <div
          className={`flex items-start gap-2 border-b border-gray-100 px-4 py-2 text-xs ${
            safetyFlag.severity === 'block'
              ? 'bg-rose-50 text-rose-900'
              : 'bg-amber-50 text-amber-900'
          }`}
        >
          {safetyFlag.severity === 'block' ? (
            <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" strokeWidth={2.5} />
          ) : (
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" strokeWidth={2.5} />
          )}
          <span className="font-medium">{safetyFlag.message}</span>
        </div>
      )}

      {/* Metadata-only banner */}
      {changeType === 'metadata-only' && (
        <div className="flex items-center gap-2 border-b border-gray-100 bg-amber-50/50 px-4 py-2 text-xs text-amber-900">
          <Pencil size={12} strokeWidth={2.5} />
          <span className="font-medium">
            No textual change — only metadata delta.
          </span>
        </div>
      )}

      {/* Body — side-by-side diff */}
      <div className="grid grid-cols-1 gap-px bg-gray-100 md:grid-cols-2">
        {/* BEFORE */}
        <div className={`p-4 ${beforePanelBg}`}>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
            Before
          </p>
          {originalStep ? (
            <>
              <DiffText
                before={beforeText}
                after={beforeText}
                mode="base"
                diffGranularity="paragraph"
              />
              <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-gray-600">
                <span>
                  Duration: <strong>{formatDuration(originalStep.expectedDuration)}</strong>
                </span>
                {originalStep.isSafetyCritical && (
                  <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">
                    Safety-critical
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm italic text-gray-400">
              (No corresponding step — this is a new insert.)
            </p>
          )}
        </div>

        {/* AFTER */}
        <div className={`p-4 ${afterPanelBg}`}>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
            After
          </p>
          {proposedStep ? (
            <>
              {changeType === 'updated' ? (
                <DiffText
                  before={beforeText}
                  after={afterText}
                  mode="diff"
                  diffGranularity={showWordDiff ? 'word' : 'paragraph'}
                />
              ) : changeType === 'deleted' ? (
                <span className="whitespace-pre-wrap text-sm text-gray-500 line-through">
                  {afterText}
                </span>
              ) : (
                <span className="whitespace-pre-wrap text-sm text-gray-800">
                  {afterText}
                </span>
              )}
              <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-gray-600">
                {/* Duration delta */}
                {originalStep?.expectedDuration !== proposedStep.expectedDuration ? (
                  <span>
                    Duration:{' '}
                    <strong className="text-amber-700">
                      {formatDuration(originalStep?.expectedDuration ?? null)} →{' '}
                      {formatDuration(proposedStep.expectedDuration)}
                    </strong>
                  </span>
                ) : (
                  <span>
                    Duration: <strong>{formatDuration(proposedStep.expectedDuration)}</strong>
                  </span>
                )}
                {proposedStep.isSafetyCritical && (
                  <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">
                    Safety-critical
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm italic text-gray-400">
              (Step removed in this proposal.)
            </p>
          )}
        </div>
      </div>

      {/* Evidence strip */}
      {evidence.length > 0 && (
        <div className="border-t border-gray-100 p-4">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
            Evidence
          </p>
          <div className="flex flex-wrap gap-4">
            {evidence.map((item, i) => (
              <EvidenceAsset
                key={`${item.id ?? item.s3Key}-${i}`}
                item={item}
                proposalId={proposalId}
                clipAlreadyExistsForStep={hasClipForThisRow}
                canRequestClip={clipActionAllowed}
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-step checkboxes — only shown in pending_review state */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 bg-gray-50/50 px-4 py-3">
          <label
            htmlFor={viewedId}
            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
          >
            <input
              id={viewedId}
              type="checkbox"
              checked={viewed}
              onChange={(e) => onViewedChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-guild-green focus:ring-guild-green/30"
            />
            Viewed this step
          </label>
          <label
            htmlFor={approvedId}
            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
          >
            <input
              id={approvedId}
              type="checkbox"
              checked={approved}
              disabled={!viewed}
              onChange={(e) => onApprovedChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-guild-green focus:ring-guild-green/30 disabled:opacity-50"
            />
            Approve this step
          </label>
        </div>
      )}
    </div>
  );
}

